import { ColDef, ExcelStyle, SideBarDef, ValueFormatterParams } from 'ag-grid-community';
import { SegmentHierarchy } from 'src/models/AppContextModels';
import { DataStatus, DataStatusType } from 'src/models/ForecastModels';
import { CellDataType } from 'src/utils/ag-grid-utils';
import { formatUTCAsLocalReadable } from 'src/utils/date-time-utilities';

// Default widths for different types of columns in the grid
export enum ColumnWidths {
  DEFAULT = 110,
  LINE_ITEM_ID = 150,
  USER_ALIAS_COLUMN = 100,
  BUDGET_TYPE_COLUMN = 115,
  CORP_SEGMENT_CLOSED_COLUMN = 120,
  CORP_SEGMENT_COLUMN = 90,
  CORP_SEGMENT_DESCRIPTION_COLUMN = 180,
  BUSINESS_SEGMENT_CLOSED_COLUMN = 140,
  BUSINESS_SEGMENT_COLUMN = 140,
  CURRENCY_COLUMN = 100,
  TOTAL_LIFE_TIME_SPEND = 130,
  MONTH_COLUMN = 105,
  QUARTER_COLUMN = 115,
  YEAR_COLUMN = 115,
  LAST_UPDATED_AT = 170
}

// Default column definition for AgGrid
export const DefaultColDef: ColDef = {
  resizable: true,
  sortable: true,
  filter: true,
  floatingFilter: false,
  wrapHeaderText: true,
  autoHeaderHeight: true,
  minWidth: 50,
  filterParams: {
    applyMiniFilterWhileTyping: true
  }
};

const suppressToolPanel = {
  suppressRowGroups: true,
  suppressValues: true,
  suppressPivots: true,
  suppressPivotMode: true,
  suppressColumnFilter: false,
  suppressColumnSelectAll: false,
  suppressColumnExpandAll: true
};

const toolPanelBaseWidth = {
  minWidth: 180,
  maxWidth: 400,
  width: 250
};

export const forecastDataGridIcons = {
  // The key 'custom-stats' represents a custom icon for stats.
  'custom-stats': '<span class="ag-icon ag-icon-custom-stats"></span>'
};

export const SideBar: SideBarDef = {
  position: 'right',
  toolPanels: [
    {
      id: 'columns',
      labelDefault: 'Columns',
      labelKey: 'columns',
      iconKey: 'columns',
      toolPanel: 'agColumnsToolPanel',
      toolPanelParams: suppressToolPanel,
      ...toolPanelBaseWidth
    },
    {
      id: 'filters',
      labelDefault: 'Filters',
      labelKey: 'filters',
      iconKey: 'filter',
      toolPanel: 'agFiltersToolPanel',
      toolPanelParams: {
        suppressExpandAll: true,
        suppressFilterSearch: false
      },
      ...toolPanelBaseWidth
    }
  ]
};

export const RowGroupPanelShow: 'always' | 'onlyWhenGrouping' | 'never' = 'never';

export const ForecastGridFixedFields = {
  // FE metadata fields (Only FE)
  RowId: { value: 'row_id', displayName: 'Row Id' },
  IsNewFERow: { value: 'is_new_fe_row', displayName: 'Is New FE Row' },
  IsTouched: { value: 'is_touched', displayName: 'Is Edited' },
  IsEdited: { value: 'is_edited', displayName: 'Is Dirty' },
  IsActive: { value: 'is_active', displayName: 'Is Active' },
  IsSegmentEdited: { value: 'is_segment_edited', displayName: 'Is Segment Edited' },

  // ID fields
  ScenarioSeqId: { value: 'scenario_seq_id', displayName: 'Scenario Seq Id' },
  ForecastInputId: { value: 'forecast_input_id', displayName: 'Forecast Input Id' },

  XptLineItemId: { value: 'xpt_line_item_id', displayName: 'Line Item Id' },
  XptLineItemSeqId: { value: 'xpt_line_item_seq_id', displayName: 'Line Item Seq Id' },

  // Tracking fields
  IsNew: { value: 'is_new', displayName: 'Is New' },

  BudgetOwner: { value: 'budget_owner', displayName: 'Budget Owner' },
  BudgetType: { value: 'budget_type', displayName: 'Budget Type' },

  // Currency fields
  LOCAL_CURRENCY: { value: 'local_currency', displayName: 'Local Currency' },
  IS_PLANNED_IN_USD: { value: 'is_planned_in_usd', displayName: 'Is Planned In USD' },

  // Used only in Reports (Currency Calculation) - Not displayed in UI
  LOCAL_TO_USD_FX_RATE: { value: 'local_to_usd_fx_rate', displayName: 'Local to USD FX Rate' },
  USD_TO_LOCAL_FX_RATE: { value: 'usd_to_local_fx_rate', displayName: 'USD to Local FX Rate' },

  TotalLifetimeSpend: { value: 'total_lifetime_spend', displayName: 'Total Lifetime Spend' },

  UpdatedAt: { value: 'updated_at', displayName: 'Updated At' },
  UpdatedBy: { value: 'updated_by', displayName: 'Updated By' }
} as const;

export type ForecastGridFixedFieldKeys = keyof typeof ForecastGridFixedFields;
export type ForecastGridFixedFieldValues = (typeof ForecastGridFixedFields)[ForecastGridFixedFieldKeys]['value'];

export const PREFIX_FIXED_COLUMNS_FORECAST_TEMPLATE: ColDef[] = [
  {
    field: ForecastGridFixedFields.RowId.value,
    headerName: ForecastGridFixedFields.RowId.displayName,
    hide: true,
    suppressFiltersToolPanel: true,
    suppressColumnsToolPanel: true,
    cellClass: ['text-field'],
    editable: false
  },
  {
    field: ForecastGridFixedFields.IsNewFERow.value,
    headerName: ForecastGridFixedFields.IsNewFERow.displayName,
    hide: true,
    suppressFiltersToolPanel: true,
    suppressColumnsToolPanel: true,
    editable: false
  },
  {
    field: ForecastGridFixedFields.IsTouched.value,
    headerName: ForecastGridFixedFields.IsTouched.displayName,
    hide: true,
    suppressFiltersToolPanel: true,
    suppressColumnsToolPanel: true,
    editable: false
  },
  {
    field: ForecastGridFixedFields.IsEdited.value,
    headerName: ForecastGridFixedFields.IsEdited.displayName,
    hide: true,
    suppressFiltersToolPanel: true,
    suppressColumnsToolPanel: true,
    editable: false
  },
  {
    field: ForecastGridFixedFields.XptLineItemSeqId.value,
    headerName: ForecastGridFixedFields.XptLineItemSeqId.displayName,
    hide: true,
    suppressFiltersToolPanel: true,
    suppressColumnsToolPanel: true,
    cellDataType: CellDataType.NUMBER,
    editable: false
  },
  {
    field: ForecastGridFixedFields.ScenarioSeqId.value,
    headerName: ForecastGridFixedFields.ScenarioSeqId.displayName,
    hide: true,
    suppressFiltersToolPanel: true,
    suppressColumnsToolPanel: true,
    editable: false
  }
];

export const SUFFIX_FIXED_COLUMNS_FORECAST_TEMPLATE: ColDef[] = [
  {
    field: ForecastGridFixedFields.UpdatedAt.value,
    headerName: ForecastGridFixedFields.UpdatedAt.displayName,
    wrapHeaderText: true,
    hide: true,
    width: ColumnWidths.LAST_UPDATED_AT,
    editable: false,
    valueFormatter: (params: ValueFormatterParams) => formatUTCAsLocalReadable(params.value)
  },
  {
    field: ForecastGridFixedFields.UpdatedBy.value,
    headerName: ForecastGridFixedFields.UpdatedBy.displayName,
    wrapHeaderText: true,
    hide: true,
    width: ColumnWidths.DEFAULT,
    cellClass: ['text-field'],
    editable: false
  }
];

export const ForecastTemplateDataStatuses: Record<DataStatusType, DataStatus> = {
  NotLoaded: {
    status: 'NotLoaded',
    statusDisplayName: 'Not Loaded',
    statusType: 'stopped',
    statusColor: 'grey',
    tooltip: 'Data not loaded yet.'
  },
  Latest: {
    status: 'Latest',
    statusDisplayName: 'Latest',
    statusType: 'success',
    statusColor: 'green',
    tooltip: 'Data is up-to-date.'
  },
  Modified: {
    status: 'Modified',
    statusDisplayName: 'Changes detected. Submit when done.',
    statusType: 'pending',
    statusColor: 'yellow',
    tooltip: 'You have made changes to this data. Remember to submit your changes.'
  },
  Outdated: {
    status: 'Outdated',
    statusDisplayName: 'Outdated',
    statusType: 'warning',
    statusColor: 'red',
    tooltip: 'This data is outdated. Another user has made updates. Refresh to get the latest data.'
  }
};

const totalCellStyle = {
  interior: {
    color: '#F2F2F2',
    pattern: 'Solid'
  },
  font: {
    bold: true
  }
};

export const agGridCellClassToExcelStyles = [
  {
    id: 'editable-cell',
    interior: {
      color: '#FFFFED',
      pattern: 'Solid'
    }
  },
  {
    id: 'nan-cell',
    interior: {
      color: 'lightcoral' // Light coral color
    }
  },
  {
    id: 'text-field',
    dataType: 'String'
  },
  {
    id: 'number-field',
    dataType: 'Number'
  },
  {
    id: 'boolean-field',
    dataType: 'Boolean'
  },
  {
    id: 'required-cell',
    dataType: 'String',
    font: {
      bold: true
    }
  },
  {
    id: 'total-lifetime-spend-cell',
    ...totalCellStyle
  },
  {
    id: 'actual-month-cell'
  },
  {
    id: 'actual-quarter-cell',
    ...totalCellStyle
  },
  {
    id: 'actual-year-cell',
    ...totalCellStyle
  },
  {
    id: 'forecast-month-cell'
  },
  {
    id: 'forecast-quarter-cell',
    ...totalCellStyle
  },
  {
    id: 'forecast-year-cell',
    ...totalCellStyle
  }
] as ExcelStyle[];

// Define the additional accounts required for RNG business groups
export const ADDITIONAL_RING_ACCOUNT: SegmentHierarchy[] = [
  { segment_description: 'Supply Chain Expenses', segment_hierarchy: ['A5000', 'A5010', 'A5400', '53800'] },
  { segment_description: 'Prepaid Inventory', segment_hierarchy: ['A1000', 'A1010', 'A1150', 'A1170', '14400'] }
];

// Define the additional accounts required for Sales & Marketing business groups
export const ADDITIONAL_SALE_MARKETING_ACCOUNT: SegmentHierarchy[] = [
  { segment_description: 'Price Protection - Wholesale', segment_hierarchy: ['A4000', 'A4003', 'A4005', 'A4080', '40832'] }
];
